import { BlockStack, Box, Button, DataTable, FooterHelp, Link, Page, Text, Tooltip, useBreakpoints } from '@shopify/polaris';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDownloadDriverRevenue, useGetFinalDriverRevenue } from 'queries/analytics.query';
import __helpers from 'helpers/index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChessKing, faMedal } from '@fortawesome/free-solid-svg-icons';
import { PageDownIcon } from '@shopify/polaris-icons';
import DateRangePicker from 'components/dateRangePicker';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

export default function DriverReport() {
  const navigate = useNavigate();

  const [defaultDate, setDefaultDate] = useState({
    startDate: dayjs().subtract(7, 'day').startOf('day').toISOString(),
    endDate: dayjs().endOf('day').toISOString(),
  });

  /** Nhân viên */
  const { data: dataStaffRevenue, isLoading: loadingDataStaffRevenue } = useGetFinalDriverRevenue(defaultDate);
  const [entities, setEntities] = useState([]);

  const [totalInfooter, setTotalInfooter] = useState({
    totalTripManifest: 0,
    totalTripMainline: 0,
    totalTripShuttle: 0,
    totalUncollectMoney: 0,
  });

  useEffect(() => {
    setEntities(dataStaffRevenue);
  }, [dataStaffRevenue]);

  /** Gán một giao dịch nào đó vào vé nào đó */
  const currentDriverForDownload = useRef(null);
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadDriverRevenue();
  const generateDownloadLink = useCallback(async () => {
    try {
      const blob = await downloadExcelThisFilter({ ...defaultDate, user_id: currentDriverForDownload.current });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `driver_revenue-${currentDriverForDownload.current}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {}
  }, [defaultDate, currentDriverForDownload.current]);

  useEffect(() => {
    if (!__helpers.isArray(entities)) return;

    let objectCount = {
      totalTripManifest: entities.reduce((sum, item) => sum + item.total_trip_manifest, 0),
      totalTripMainline: entities.reduce((sum, item) => sum + item.total_trip_mainline, 0),
      totalTripShuttle: entities.reduce((sum, item) => sum + item.total_trip_shuttle, 0),
      totalUncollectMoney: entities.reduce((sum, item) => sum + item.money_uncollected_total, 0),
    };

    setTotalInfooter(objectCount);
  }, [entities]);

  /**
   * Date time filling ...
   */
  const dateChangeCallback = useCallback((_e) => {
    const { since, until } = _e.period;

    setDefaultDate({
      startDate: since ? dayjs(since).startOf('day').toISOString() : '',
      endDate: until ? dayjs(until).endOf('day').toISOString() : '',
    });
  }, []);

  let { lgDown } = useBreakpoints();
  const fixedFirstColumns = lgDown ? 1 : 0;

  const handleSort = useCallback(
    (index, direction) => {
      let _direction = direction === 'descending' ? 'desc' : 'asc';
      switch (index) {
        case 2:
          setEntities(
            entities
              .slice()
              .sort((a, b) => (_direction === 'asc' ? a.total_trip_manifest - b.total_trip_manifest : b.total_trip_manifest - a.total_trip_manifest))
          );
          break;
        case 3:
          setEntities(
            entities
              .slice()
              .sort((a, b) => (_direction === 'asc' ? a.total_trip_mainline - b.total_trip_mainline : b.total_trip_mainline - a.total_trip_mainline))
          );
          break;
        case 4:
          setEntities(
            entities
              .slice()
              .sort((a, b) => (_direction === 'asc' ? a.total_trip_shuttle - b.total_trip_shuttle : b.total_trip_shuttle - a.total_trip_shuttle))
          );
          break;
        case 7:
          setEntities(
            entities
              .slice()
              .sort((a, b) =>
                _direction === 'asc' ? a.money_uncollected_total - b.money_uncollected_total : b.money_uncollected_total - a.money_uncollected_total
              )
          );
          break;
      }
    },
    [entities]
  );

  return (
    <div>
      <Helmet>
        <title>Báo cáo lái xe</title>
      </Helmet>
      <Page title="Báo cáo hiệu suất lái xe" subtitle={`Khoảng thời gian: ${defaultDate.startDate} - ${defaultDate.endDate}`}>
        <BlockStack gap="400">
          <Box>
            <DateRangePicker onChange={dateChangeCallback} />
            <br />

            {loadingDataStaffRevenue ? (
              <Text as="p">Đang tải dữ liệu...</Text>
            ) : (
              <DataTable
                sortable={[false, false, true, true, true, false, false, true]}
                initialSortColumnIndex={1}
                defaultSortDirection="descending"
                onSort={handleSort}
                hideScrollIndicator
                fixedFirstColumns={fixedFirstColumns}
                columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                headings={[
                  'Rank',
                  'Nhân viên',
                  'Tổng chuyến',
                  'Chuyến chính',
                  'Trung chuyển',
                  'Chuyến lái chính',
                  'Chuyến lái phụ',
                  'Chưa nộp lệnh',
                  'Workload',
                  '--',
                ]}
                rows={
                  entities
                    ? entities?.map((ele) => {
                        return [
                          ele.rank,
                          <div>
                            {ele.rank === 1 ? <FontAwesomeIcon icon={faChessKing} /> : null}
                            {ele.rank === 2 ? <FontAwesomeIcon icon={faMedal} /> : null}
                            &nbsp;
                            {`${ele.display_name} (${ele.user_phonenumber || 'no_phone'})`}
                          </div>,
                          ele.total_trip_manifest,
                          ele.total_trip_mainline,
                          ele.total_trip_shuttle,
                          __helpers.formatNumberWithCommas(ele.total_as_driver),
                          __helpers.formatNumberWithCommas(ele.total_as_assistant),
                          __helpers.formatNumberWithCommas(ele.money_uncollected_total),
                          ele.workload_percent + '%',

                          <Tooltip content="Xuất báo cáo chuyến đi của lái xe này">
                            <Button
                              variant="monochromePlain"
                              onClick={() => {
                                currentDriverForDownload.current = ele.user_id;
                                generateDownloadLink();
                              }}
                            >
                              xuất báo cáo
                            </Button>
                          </Tooltip>,
                        ];
                      })
                    : []
                }
                totals={[
                  '-',
                  '-',
                  __helpers.formatNumberWithCommas(totalInfooter.totalTripManifest),
                  __helpers.formatNumberWithCommas(totalInfooter.totalTripMainline),
                  __helpers.formatNumberWithCommas(totalInfooter.totalTripShuttle),
                  '',
                  '',
                  __helpers.formatNumberWithCommas(totalInfooter.totalUncollectMoney),
                  '--',
                  '--',
                ]}
                showTotalsInFooter
                totalsName={{ singular: 'Tổng', plural: 'Tổng' }}
                hasZebraStripingOnData
                increasedTableDensity
              />
            )}
          </Box>
          <FooterHelp align="start">
            <Text as="p" variant="bodySm" tone="subdued">
              * Workload Score là điểm khối lượng công việc của tài xế, được tính từ số chuyến hoàn thành trong kỳ (Mainline = 1.0 điểm, Shuttle = 0.3
              điểm). Điểm càng cao cho thấy mức độ tham gia vận hành càng lớn.
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              * Lưu ý: Chỉ số này phản ánh khối lượng công việc, không phản ánh chất lượng phục vụ hoặc mức độ an toàn khi vận hành.
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              * Ở ĐÂY CHỈ HIỂN THỊ CÁC CHUYẾN HOÀN THÀNH
            </Text>
          </FooterHelp>
        </BlockStack>
      </Page>
    </div>
  );
}

